// DashboardRecentActivity.jsx - Recent Activity Component
import React, { useState, useEffect } from 'react';
import supabase from '../../supabase';
import './DashboardRecentActivity.css';

const DashboardRecentActivity = ({ user }) => {
  const [recentActivities, setRecentActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    if (user) {
      fetchRecentActivities();
    }
  }, [user]);

  const fetchRecentActivities = async () => {
    try {
      setLoading(true);
      setHasError(false);

      const activities = await fetchActivitiesData();
      setRecentActivities(activities);

    } catch (error) {
      console.error('Error fetching recent activities:', error);
      setHasError(true);
      setRecentActivities([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchActivitiesData = async () => {
    try {
      const { data: invoices, error: invoicesError } = await supabase
        .from('invoices')
        .select('id, invoice_number, status, created_at, updated_at, client_id, total_amount')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(10);

      if (invoicesError) {
        console.error('Error fetching invoice activities:', invoicesError);
      }

      const { data: clients, error: clientsError } = await supabase
        .from('clients')
        .select('id, client_name')
        .eq('user_id', user.id);

      if (clientsError) {
        console.error('Error fetching clients:', clientsError);
      }

      const clientMap = {};
      if (clients) {
        clients.forEach(client => {
          clientMap[client.id] = client.client_name;
        });
      }

      const activities = [];

      if (invoices && invoices.length > 0) {
        invoices.forEach(invoice => {
          const clientName = clientMap[invoice.client_id] || 'Unknown Client';
          const amount = invoice.total_amount ? `₹${parseFloat(invoice.total_amount).toLocaleString('en-IN')}` : '';
          
          activities.push({
            id: `invoice-${invoice.id}`,
            type: 'invoice',
            icon: getActivityIcon(invoice.status),
            text: `Invoice #${invoice.invoice_number || 'N/A'} ${getActivityAction(invoice.status)} for ${clientName}${amount ? ` - ${amount}` : ''}`,
            time: formatTimeAgo(invoice.created_at),
            timestamp: new Date(invoice.created_at),
            status: invoice.status
          });
        });
      }

      return activities
        .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
        .slice(0, 5);
    } catch (error) {
      console.error('Error in fetchActivitiesData:', error);
      return [];
    }
  };

  const getActivityIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'paid': return '✅';
      case 'sent': return '📤';
      case 'draft': return '📝';
      case 'overdue': return '⚠️';
      default: return '📄';
    }
  };

  const getActivityAction = (status) => {
    switch (status?.toLowerCase()) {
      case 'paid': return 'was paid';
      case 'sent': return 'was sent';
      case 'draft': return 'created';
      case 'overdue': return 'is overdue';
      default: return 'updated';
    }
  };

  const formatTimeAgo = (dateString) => {
    if (!dateString) return 'Unknown';
    
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffInSeconds = Math.floor((now - date) / 1000);

      if (diffInSeconds < 60) return 'Just now';
      if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
      if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
      if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
      return date.toLocaleDateString();
    } catch (error) {
      return 'Unknown';
    }
  };

  if (loading) {
    return (
      <div className="dashboard-recent-activity">
        <h2 className="dashboard-recent-activity-section-title">Recent Activity</h2>
        <div className="dashboard-recent-activity-loading">
          <div className="dashboard-recent-activity-loading-spinner"></div>
          <p>Loading recent activities...</p>
        </div>
      </div>
    );
  }

  if (hasError) {
    return (
      <div className="dashboard-recent-activity">
        <h2 className="dashboard-recent-activity-section-title">Recent Activity</h2>
        <div className="dashboard-recent-activity-error">
          <p>Failed to load recent activities. Please try again.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-recent-activity">
      <h2 className="dashboard-recent-activity-section-title">Recent Activity</h2>
      <div className="dashboard-recent-activity-list">
        {recentActivities.length > 0 ? (
          recentActivities.map((activity) => (
            <div 
              key={activity.id} 
              className="dashboard-recent-activity-item"
              role="button"
              tabIndex={0}
              onKeyPress={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  // Add click handler if needed
                }
              }}
            >
              <div className="dashboard-recent-activity-icon">
                {activity.icon}
              </div>
              <div className="dashboard-recent-activity-content">
                <p className="dashboard-recent-activity-text">{activity.text}</p>
                <span className="dashboard-recent-activity-time">{activity.time}</span>
              </div>
            </div>
          ))
        ) : (
          <div className="dashboard-recent-activity-no-activity">
            <div className="dashboard-recent-activity-no-activity-icon">📝</div>
            <p>No recent activity found. Start by creating your first invoice!</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardRecentActivity;
